import type { CallsClient } from '@wtfalch/calls';

import { eventCallRoomId, eventCallWindow, guestEntryName } from './eventCalls.ts';
import { guestLinkUrl } from './guestLink.ts';

const CAPACITY = 25;

export type EventCallSetup = {
  sessionId: string;
  /** The guest link address and the `virtualLocations` entry that holds it. */
  guestUrl: string;
  guestEntry: { uri: string; name: string };
};

/**
 * Makes the call an event carries: a session for room `event:<uid>` open from
 * ten minutes before the start to an hour after the end, and a guest link for
 * the same window. Nothing is left half-made: a link that cannot be made ends
 * the session it was for.
 */
export async function createEventCall(
  client: CallsClient,
  origin: string,
  uid: string,
  startMs: number,
  endMs: number,
): Promise<EventCallSetup> {
  const { validFrom, expiresAt } = eventCallWindow(startMs, endMs);
  const sessionId = crypto.randomUUID();
  await client.createSession({
    sessionId,
    roomId: eventCallRoomId(uid),
    validFrom,
    expiresAt,
    capacity: CAPACITY,
    requestId: crypto.randomUUID(),
  });
  try {
    const link = await client.createGuestLink(sessionId, {
      validFrom,
      expiresAt,
      maxRedemptions: CAPACITY,
      requestId: crypto.randomUUID(),
    });
    if (!link.secret) throw new Error('The guest link could not be made.');
    const guestUrl = guestLinkUrl(origin, link.secret);
    return {
      sessionId,
      guestUrl,
      guestEntry: { uri: guestUrl, name: guestEntryName(sessionId, link.linkId) },
    };
  } catch (error) {
    await client
      .endSession(sessionId, { requestId: crypto.randomUUID() })
      .catch((endError: unknown) => console.error('Could not end the half-made call:', endError));
    throw error;
  }
}

/** Turns an event's call off: the guest link stops working and the session
 *  ends. Both are tried; the first failure is the one reported. */
export async function endEventCall(
  client: CallsClient,
  entry: { sessionId: string; linkId: string },
): Promise<void> {
  const results = await Promise.allSettled([
    client.revokeGuestLink(entry.sessionId, {
      linkId: entry.linkId,
      requestId: crypto.randomUUID(),
    }),
    client.endSession(entry.sessionId, { requestId: crypto.randomUUID() }),
  ]);
  const failed = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (failed) throw failed.reason;
}
